import { useEffect, useRef, useState } from 'react'

interface UseNearEndSentinelOptions {
  onReached: () => void
  disabled?: boolean
  rootMargin?: string
}

/**
 * Ref callback for an end-of-list sentinel element; fires onReached whenever the
 * sentinel is within rootMargin of the viewport.
 *
 * The observer is re-created every time `disabled` flips back to false: observe()
 * always reports the current intersection state, so if the sentinel is still in
 * range after a fetch completes, the next fetch starts immediately (content shorter
 * than the viewport keeps loading until it isn't).
 */
export const useNearEndSentinel = ({
  onReached,
  disabled = false,
  rootMargin = '1200px',
}: UseNearEndSentinelOptions): ((el: HTMLElement | null) => void) => {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const onReachedRef = useRef(onReached)
  onReachedRef.current = onReached

  useEffect(() => {
    if (!element || disabled || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onReachedRef.current()
      },
      { rootMargin }
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [element, disabled, rootMargin])

  return setElement
}
